/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                              - RETURNS PORTAL -                              |
|______________________________________________________________________________|
*/

class ReturnsPortal extends HTMLElement {
  connectedCallback() {
    // prevent double-binding if Shopify editor reuses the node
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.endpoint = this.dataset.endpoint;

    this.form = this.querySelector('[data-form]');
    this.error = this.querySelector('[data-error]');
    this.results = this.querySelector('[data-results]');
    this.back = this.querySelector('[data-back]');
    this.responseOrder = this.querySelector('[data-response-order]');
    this.responseEmail = this.querySelector('[data-response-email]');
    this.returnLink = this.querySelector('[data-return-link]');

    if (!this.form || !this.endpoint) return;

    this.onSubmit = this.onSubmit.bind(this);
    this.onBack = this.onBack.bind(this);

    this.form.addEventListener('submit', this.onSubmit);
    this.back?.addEventListener('click', this.onBack);
  }

  setHidden(el, hidden) {
    if (el) el.hidden = !!hidden;
  }

  resetUI() {
    this.setHidden(this.error, true);
    this.setHidden(this.results, true);
    this.setHidden(this.back, true);
    this.setHidden(this.form, false);
  }

  async onSubmit(e) {
    e.preventDefault();

    this.setHidden(this.error, true);
    this.setHidden(this.results, true);
    this.setHidden(this.back, true);

    const order_number = (this.form.querySelector('input[name="order_number"]')?.value || '').trim();
    const email = (this.form.querySelector('input[name="email"]')?.value || '').trim();

    if (!order_number || !email) {
      this.setHidden(this.error, false);
      return;
    }

    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, order_number })
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) throw new Error('Non-JSON response');

      const data = await res.json();

      if (res.ok && data?.success === true && data?.dhl_return_url) {
        this.setHidden(this.form, true);
        this.setHidden(this.error, true);
        this.setHidden(this.results, false);
        this.setHidden(this.back, false);

        if (this.responseEmail) this.responseEmail.textContent = data?.order_details?.email || '';
        if (this.responseOrder) this.responseOrder.textContent = data?.order_details?.order_number || '';
        if (this.returnLink) this.returnLink.href = data.dhl_return_url;
        return;
      }

      this.setHidden(this.error, false);
    } catch (err) {
      this.setHidden(this.error, false);
    }
  }

  onBack() {
    this.resetUI();
  }
}

if (!customElements.get('returns-portal')) {
  customElements.define('returns-portal', ReturnsPortal);
}

/*
|‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾‾|
|                              - CUSTOM LOCALIZATION FORM -                              |
|________________________________________________________________________________________|
*/

(() => {
  const ROOT_SELECTOR = '[data-custom-localization]';

  class CustomLocalizationForm {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      this.selectorRoots = Array.from(root.querySelectorAll('[data-selector-root]'));
      this.toggles = Array.from(root.querySelectorAll('[data-localization-toggle]'));
      this.options = Array.from(root.querySelectorAll('[data-localization-option]'));
      this.searchInputs = Array.from(root.querySelectorAll('[data-localization-search]'));
      this.nativeSelects = Array.from(root.querySelectorAll('[data-localization-native]'));

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    }

    init() {
      if (!this.form || this.root.dataset.customLocalizationReady === 'true') return;

      this.root.dataset.customLocalizationReady = 'true';

      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const selector = toggle.closest('[data-selector-root]');
          if (!selector) return;

          if (selector.classList.contains('is-open')) {
            this.closeSelector(selector);
          } else {
            this.closeAllSelectors(selector);
            this.openSelector(selector);
          }
        });
      });

      this.options.forEach((option) => {
        option.addEventListener('click', () => {
          const inputName = option.dataset.inputName;
          const value = option.dataset.value;
          const input = this.form.querySelector(`[name="${inputName}"]`);

          if (!input) return;

          input.value = value;
          this.form.submit();
        });
      });

      this.nativeSelects.forEach((select) => {
        select.addEventListener('change', () => {
          this.form.submit();
        });
      });

      this.searchInputs.forEach((input) => {
        input.addEventListener('input', () => {
          const panel = input.closest('[data-localization-panel]');
          if (!panel) return;

          const query = input.value.trim().toLowerCase();
          const options = Array.from(panel.querySelectorAll('[data-localization-option]'));
          const noResults = panel.querySelector('[data-localization-no-results]');
          let visibleCount = 0;

          options.forEach((option) => {
            const haystack = (option.dataset.searchText || '').toLowerCase();
            const match = !query || haystack.includes(query);
            option.hidden = !match;

            if (match) visibleCount += 1;
          });

          if (noResults) {
            noResults.hidden = visibleCount !== 0;
          }
        });
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }

    openSelector(selector) {
      const toggle = selector.querySelector('[data-localization-toggle]');
      const panel = selector.querySelector('[data-localization-panel]');
      if (!toggle || !panel) return;

      selector.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      const search = panel.querySelector('[data-localization-search]');
      if (search) search.focus();
    }

    closeSelector(selector) {
      const toggle = selector.querySelector('[data-localization-toggle]');
      const panel = selector.querySelector('[data-localization-panel]');
      if (!toggle || !panel) return;

      selector.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    }

    closeAllSelectors(except = null) {
      this.selectorRoots.forEach((selector) => {
        if (except && selector === except) return;
        this.closeSelector(selector);
      });
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) {
        this.closeAllSelectors();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeAllSelectors();
      }
    }
  }

  function initCustomLocalizationForms(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance = new CustomLocalizationForm(root);
      instance.init();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCustomLocalizationForms(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initCustomLocalizationForms(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    initCustomLocalizationForms(event.target);
  });
})();
